import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Download, Search, RefreshCw, Pencil, Camera, Zap } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { API, authFetch } from '../utils/api';

const LOCATIONS = [
  { value: 'osf', label: 'OSF' },
  { value: 'civic_center', label: 'Civic Center' },
  { value: 'civic_center_west', label: 'Civic Center West' },
  { value: 'great_park', label: 'Great Park' }
];

const WASH_TYPES = [
  { value: 'wash_only_doors_locked', label: 'Wash Only Doors Locked' },
  { value: 'interior_and_wash', label: 'Interior & Wash' },
  { value: 'wash_only', label: 'Wash Only' },
  { value: 'interior_only', label: 'Interior Only' },
  { value: 'not_done', label: 'Not Done' }
];

const getLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function WashHistory({ user }) {
  const [history, setHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWash, setEditingWash] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    vehicle_id: '',
    wash_date: getLocalDate(),
    wash_type: '',
    location: '',
    notes: ''
  });
  const [scanning, setScanning] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scannedText, setScannedText] = useState('');
  const [flashOn, setFlashOn] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanLoopRef = useRef(null);
  const workerRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    fetchHistory();
    fetchVehicles();
    const interval = setInterval(() => { fetchHistory(); fetchVehicles(); }, 30000);
    const onFocus = () => { fetchHistory(); fetchVehicles(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchHistory = async () => {
    try {
      const response = await authFetch(`${API}/history`, {
      });
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await authFetch(`${API}/vehicles`, {
      });
      if (!response.ok) throw new Error('Failed to fetch vehicles');
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
  };

  const matchVehicle = (text) => {
    const cleaned = text.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
    const tokens = cleaned.split(/\s+/).filter(t => t.length >= 2);
    for (const token of tokens) {
      const t = token.toLowerCase();
      const found = vehicles.find(v =>
        v.unit_number && v.unit_number.toLowerCase() === t
      );
      if (found) return found;
    }
    for (const token of tokens) {
      const t = token.toLowerCase();
      const found = vehicles.find(v =>
        v.unit_number && v.unit_number.toLowerCase().includes(t)
      );
      if (found) return found;
    }
    return null;
  };

  const startAutoScan = async () => {
    setScanDialogOpen(true);
    setScanning(true);
    setScannedText('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;

      // Wait for video element to be in DOM
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        // Start OCR loop
        runScanLoop();
      }, 500);
    } catch (err) {
      toast.error('Could not access camera');
      stopAutoScan();
    }
  };

  const runScanLoop = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const { createWorker } = await import('tesseract.js');
    const worker = await createWorker('eng');
    workerRef.current = worker;

    const scan = async () => {
      if (!videoRef.current || !streamRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!canvas || video.readyState < 2) {
        scanLoopRef.current = setTimeout(scan, 1000);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      try {
        const { data: { text } } = await worker.recognize(canvas);
        // Extract only numbers from the scanned text
        const numbers = text.match(/\d+/g) || [];
        const displayText = numbers.join(', ');
        if (displayText) setScannedText(displayText);

        const matched = matchVehicle(text);
        if (matched) {
          toast.success(`Found: Unit ${matched.unit_number}`);
          setFormData(prev => ({ ...prev, vehicle_id: matched.vehicle_id }));
          await stopAutoScan();
          setDialogOpen(true);
          return;
        }
      } catch {}

      // Continue scanning every 2 seconds
      scanLoopRef.current = setTimeout(scan, 2000);
    };

    scanLoopRef.current = setTimeout(scan, 1000);
  };

  const stopAutoScan = async () => {
    if (scanLoopRef.current) { clearTimeout(scanLoopRef.current); scanLoopRef.current = null; }
    if (workerRef.current) { try { await workerRef.current.terminate(); } catch {} workerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
    setScanDialogOpen(false);
    setFlashOn(false);
  };

  const toggleFlash = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    try {
      const newFlash = !flashOn;
      await track.applyConstraints({ advanced: [{ torch: newFlash }] });
      setFlashOn(newFlash);
    } catch {
      toast.error('Flash not available on this device');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_id || !formData.wash_type || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check for duplicate (same vehicle in same month) - only for new entries
    if (!editingWash) {
      const washMonth = formData.wash_date.slice(0, 7); // YYYY-MM
      const duplicate = history.find(h => {
        const existingMonth = (h.wash_date || '').slice(0, 7);
        return h.vehicle_id === formData.vehicle_id && existingMonth === washMonth;
      });
      
      if (duplicate) {
        toast.error('This vehicle already has a wash recorded for this month');
        return;
      }
    }

    try {
      const url = editingWash 
        ? `${API}/history/${editingWash.history_id}`
        : `${API}/history`;
      const method = editingWash ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          wash_date: new Date(formData.wash_date).toISOString(),
          cost: 0,
          duration_minutes: 0
        })
      });

      if (!response.ok) throw new Error('Failed to save history');

      toast.success(editingWash ? 'Wash updated successfully' : 'Wash recorded successfully');
      setDialogOpen(false);
      setEditingWash(null);
      setFormData({ vehicle_id: '', wash_date: getLocalDate(), wash_type: '', location: '', notes: '' });
      setVehicleSearchQuery('');
      fetchHistory();
    } catch (error) {
      toast.error(editingWash ? 'Failed to update wash' : 'Failed to record wash');
    }
  };

  const handleEdit = (wash) => {
    setEditingWash(wash);
    setFormData({
      vehicle_id: wash.vehicle_id || '',
      wash_date: wash.wash_date ? wash.wash_date.slice(0, 10) : getLocalDate(),
      wash_type: wash.wash_type || '',
      location: wash.location || '',
      notes: wash.notes || ''
    });
    setDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const response = await authFetch(`${API}/export/history`, {
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'monthly_wash_history.xlsx';
      a.click();
      toast.success('Exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleRefresh = async () => {
    if (!window.confirm('Are you sure you want to clear all wash history? This action cannot be undone.')) return;
    
    try {
      const response = await authFetch(`${API}/history/clear-all`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to clear history');
      
      toast.success('All wash history cleared');
      setHistory([]);
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const getVehicle = (vehicleId) => {
    return vehicles.find(v => v.vehicle_id === vehicleId) || {};
  };

  const getLocationLabel = (value) => {
    const location = LOCATIONS.find(l => l.value === value);
    return location ? location.label : value || '-';
  };

  const getWashTypeLabel = (value) => {
    const washType = WASH_TYPES.find(w => w.value === value);
    return washType ? washType.label : value || '-';
  };

  // Filter vehicles for the form search
  const filteredVehiclesForForm = vehicles.filter(vehicle => {
    if (!vehicleSearchQuery) return true;
    const query = vehicleSearchQuery.toLowerCase();
    return (
      (vehicle.unit_number && vehicle.unit_number.toLowerCase().includes(query)) ||
      (vehicle.license_plate && vehicle.license_plate.toLowerCase().includes(query)) ||
      (vehicle.make && vehicle.make.toLowerCase().includes(query)) ||
      (vehicle.model && vehicle.model.toLowerCase().includes(query))
    );
  });

  const filteredHistory = history.filter(item => {
    const query = searchQuery.toLowerCase();
    const vehicle = vehicles.find(v => v.vehicle_id === item.vehicle_id);
    const vehicleInfo = vehicle ? `${vehicle.unit_number || ''} ${vehicle.license_plate || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.toLowerCase() : '';
    const locationLabel = getLocationLabel(item.location).toLowerCase();
    const washTypeLabel = getWashTypeLabel(item.wash_type).toLowerCase();
    
    return (
      vehicleInfo.includes(query) ||
      locationLabel.includes(query) ||
      washTypeLabel.includes(query)
    );
  });

  const getSelectedVehicleDisplay = () => {
    if (!formData.vehicle_id) return '';
    const vehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);
    if (!vehicle) return '';
    return `${vehicle.unit_number || '-'} - ${vehicle.license_plate || '-'}`;
  };

  const isAdmin = user?.role === 'administrator' || user?.role === 'admin';

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="history-title">Monthly Wash History</h1>
          </div>
          <div className="flex flex-wrap gap-2 lg:gap-3">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={handleRefresh} data-testid="refresh-history-button" className="text-red-600 border-red-200 hover:bg-red-50 text-xs lg:text-sm">
                  <RefreshCw className="h-4 w-4 mr-1 lg:mr-2" />
                  REFRESH
                </Button>
                <Button variant="outline" onClick={handleExport} data-testid="export-history-button" className="text-xs lg:text-sm">
                  <Download className="h-4 w-4 mr-1 lg:mr-2" />
                  EXPORT
                </Button>
              </>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (open && !editingWash) {
                setFormData(prev => ({ ...prev, wash_date: getLocalDate() }));
              }
              if (!open) {
                setVehicleSearchQuery('');
                setEditingWash(null);
                setFormData({ vehicle_id: '', wash_date: getLocalDate(), wash_type: '', location: '', notes: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-wash-button">
                  <Plus className="h-4 w-4 mr-2" />
                  ADD WASH
                </Button>
              </DialogTrigger>
              <Button
                variant="outline"
                onClick={startAutoScan}
                disabled={scanning}
                data-testid="scan-button"
                className="text-xs lg:text-sm"
              >
                <Camera className="h-4 w-4 mr-1 lg:mr-2" />
                {scanning ? 'SCANNING...' : 'SCAN'}
              </Button>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">{editingWash ? 'Edit Wash' : 'Record Wash'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Vehicle</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        data-testid="vehicle-search-form-input"
                        type="text"
                        placeholder="Search vehicle by unit #, license plate..."
                        value={vehicleSearchQuery}
                        onChange={(e) => setVehicleSearchQuery(e.target.value)}
                        className="pl-9 bg-slate-50 border-slate-200"
                      />
                    </div>
                    {formData.vehicle_id && (
                      <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
                        <span className="text-sm font-semibold text-green-700">
                          Selected: {getSelectedVehicleDisplay()}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, vehicle_id: '' })}
                          className="text-red-500 h-6 px-2"
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md bg-white">
                      {filteredVehiclesForForm.length === 0 ? (
                        <div className="p-3 text-center text-slate-500 text-sm">
                          {vehicleSearchQuery ? 'No vehicles found' : 'No vehicles available'}
                        </div>
                      ) : (
                        filteredVehiclesForForm.map((vehicle) => (
                          <div
                            key={vehicle.vehicle_id}
                            onClick={() => setFormData({ ...formData, vehicle_id: vehicle.vehicle_id })}
                            className={`p-3 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors ${
                              formData.vehicle_id === vehicle.vehicle_id ? 'bg-sky-50 border-l-4 border-l-sky-500' : ''
                            }`}
                            data-testid={`vehicle-option-${vehicle.vehicle_id}`}
                          >
                            <div className="font-semibold text-sm">
                              {vehicle.unit_number || '-'} - {vehicle.license_plate || '-'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {vehicle.make} {vehicle.model}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Wash Type</Label>
                    <Select value={formData.wash_type} onValueChange={(value) => setFormData({ ...formData, wash_type: value })} required>
                      <SelectTrigger data-testid="wash-type-select" className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Select wash type" />
                      </SelectTrigger>
                      <SelectContent>
                        {WASH_TYPES.map((washType) => (
                          <SelectItem key={washType.value} value={washType.value}>
                            {washType.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Location</Label>
                    <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })} required>
                      <SelectTrigger data-testid="wash-location-select" className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATIONS.map((location) => (
                          <SelectItem key={location.value} value={location.value}>
                            {location.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Notes</Label>
                    <Input
                      data-testid="wash-notes-input"
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Add any notes..."
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Date</Label>
                    <Input
                      data-testid="wash-date-input"
                      type="date"
                      value={formData.wash_date}
                      onChange={(e) => setFormData({ ...formData, wash_date: e.target.value })}
                      required
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <Button type="submit" className="w-full uppercase font-bold" data-testid="wash-submit-button">
                    {editingWash ? 'Update Wash' : 'Record Wash'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by vehicle, location, or wash type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50 border-slate-200"
              data-testid="wash-search-input"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-slate-600 mt-2">
              Found {filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Unit #</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">License Plate #</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Make</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Model</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Wash Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Notes</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Edit</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                      {searchQuery ? `No records found matching "${searchQuery}"` : 'No wash history found'}
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item, index) => {
                    const vehicle = getVehicle(item.vehicle_id);
                    return (
                      <tr key={item.history_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`history-row-${index}`}>
                        <td className="px-4 py-4 text-sm">{moment(item.wash_date).format('MMM DD, YYYY')}</td>
                        <td className="px-4 py-4 font-mono font-semibold text-sm">{vehicle.unit_number || '-'}</td>
                        <td className="px-4 py-4 font-mono text-sm">{vehicle.license_plate || '-'}</td>
                        <td className="px-4 py-4 text-sm">{vehicle.make || '-'}</td>
                        <td className="px-4 py-4 text-sm">{vehicle.model || '-'}</td>
                        <td className="px-4 py-4 text-sm">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                            item.wash_type === 'not_done' 
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : item.wash_type === 'interior_and_wash'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-slate-50 text-slate-700 border-slate-200'
                          }`}>
                            {getWashTypeLabel(item.wash_type)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            {getLocationLabel(item.location)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600">{item.notes || '-'}</td>
                        <td className="px-4 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            data-testid={`edit-wash-${index}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Live Camera Scanner Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={(open) => { if (!open) stopAutoScan(); }}>
        <DialogContent className="max-w-sm p-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Scan Vehicle</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Point camera at unit number</p>
          <div className="relative rounded-lg overflow-hidden bg-black" data-testid="camera-view">
            <video ref={videoRef} playsInline muted autoPlay className="w-full rounded-lg" style={{ maxHeight: 300 }} />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-green-400 rounded-md w-3/4 h-16 opacity-70"></div>
            </div>
            <button
              onClick={toggleFlash}
              className={`absolute top-2 right-2 p-2 rounded-full ${flashOn ? 'bg-yellow-400 text-black' : 'bg-black/50 text-white'}`}
              data-testid="flash-button"
            >
              <Zap className="h-5 w-5" />
            </button>
            {scanning && (
              <div className="absolute bottom-2 left-0 right-0 text-center">
                <span className="bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                  {scannedText ? `Reading: ${scannedText}` : 'Scanning...'}
                </span>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={stopAutoScan} className="w-full">Cancel</Button>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
